/**
 * Tunable constants for the reading-progression mode.
 * See specs/word-mastery-progression.md §7. Pedagogy lives here, not scattered in code.
 */
import { GALAXY_STARS, ECLIPSE_STARS, SUN_RED_STARS, SUN_VIOLET_STARS } from '@/game/milestones';
import { STAR_STACK_SLOTS } from '@/game/config';

/** Words per language pool. */
export const POOL_SIZE = 250;

/**
 * Floor for the active (introduced, not-yet-mastered) words kept available *per drill class*, so
 * every board type (noun/verb/adjective) has enough variety and the same words don't repeat.
 *
 * Was 6, which made a board a 4-of-6 draw from its topic — the same words came back almost every
 * turn of that topic. At 10 the draw is 4-of-10, and with `maxActiveDrillClasses` topics unlocked
 * it puts ~60–70 words in circulation instead of 24.
 *
 * A floor, not the quota: what a game actually needs is a *circulation* size, and a lesson with
 * few topics reaches far fewer words than one with seven. See activePerDrillClass().
 */
export const ACTIVE_PER_DRILL_CLASS = 10;

/**
 * Distinct words a game needs in circulation for MAX_SIGHTINGS_PER_GAME to be reachable at all.
 *
 * A board shows 8 tiles, so N boards need `N × 8 / MAX_SIGHTINGS_PER_GAME` distinct words before
 * the ceiling becomes arithmetically impossible — 60 words ⇒ it holds to ~35 boards in one game.
 *
 * The per-class quota alone did not deliver that. It was measured per *class*, so circulation was
 * `unlocked topics × ACTIVE_PER_DRILL_CLASS` and the three lessons with few drill classes fell far
 * short of 60: reading-250 and Der-Die-Das have 3, fables 4, against the 7 the gated pools unlock.
 * Measured over 40 seeded games at 90% accuracy, that is exactly where the ceiling broke —
 * Der-Die-Das (30 words circulating) showed a word 7 times in *every* 20-board game and up to 11
 * times in 30 boards; reading-250 (36) reached 8; fables (41) reached 7. The three lessons at
 * ~60–70 words stayed at 5, breaching to 6 only past 30 boards. The ceiling was never the problem:
 * OVER_SOFT_CAP_WEIGHT is a *relative* multiplier, so once every candidate of a class is over the
 * cap, ×0.01 across the board cancels out and drawable()'s "filling the board wins" bypass then
 * lets the hard ceiling go too.
 */
export const TARGET_CIRCULATION = 60;

/**
 * Active words to keep per drill class, given how many are currently unlocked: enough of them to
 * put TARGET_CIRCULATION words in play, but never below the ACTIVE_PER_DRILL_CLASS floor.
 *
 * The gated 7-topic pools (Daily Life, Adventure, Picture Words) land on the floor and are
 * unchanged; the narrow lessons rise — fables 4 topics ⇒ 15, reading-250 and Der-Die-Das 3 ⇒ 20.
 */
export function activePerDrillClass(unlockedDrillClasses: number): number {
  if (unlockedDrillClasses <= 0) return ACTIVE_PER_DRILL_CLASS;
  if (unlockedDrillClasses === 2) return TWO_CLASS_QUOTA;
  return Math.max(ACTIVE_PER_DRILL_CLASS, Math.ceil(TARGET_CIRCULATION / unlockedDrillClasses));
}

/**
 * The two-class case, which the formula above gets wrong — measured on "i oder ie?", the first
 * lesson to have only two (specs/ie-lesson.md).
 *
 * TARGET_CIRCULATION counts the words in play, and by that measure two classes of 30 are fine: 60,
 * the same as Der-Die-Das's three of 20. But a board draws its four targets from ONE class and its
 * four decoys from all the OTHERS, and that second pool is where two classes are thin — 30 words
 * against Der-Die-Das's 40 and a gated lesson's 60. The sampler feels it: over 30 seeds of 30
 * boards the formula's 30 showed some word a sixth time in **18 of 30 games**, against a budget of
 * half (variety.test.ts). At 40 no seed breaches.
 *
 * Not raised further, although the decoy argument would suggest 60: the quota is also the RAMP.
 * ensureActive keeps this many unmastered words open per class, so 60 would deal a fresh profile
 * the whole of stage 3 — the Merkwörter — on its first board, which the lesson's stage order exists
 * to prevent.
 */
const TWO_CLASS_QUOTA = 40;

/** Active function words (articles/pronouns/…) to keep introduced for decoys + sight-word exposure. */
export const ACTIVE_FUNCTION_WORDS = 6;

/** Mastered words per earned star. */
export const WORDS_PER_STAR = 10;

/** Total stars on the journey (POOL_SIZE / WORDS_PER_STAR). Reaching all → ABC Planet. */
export const STAR_COUNT = POOL_SIZE / WORDS_PER_STAR; // 25

/**
 * Stars earned at a given mastery — the ONE definition, used by the stats snapshot, the
 * pre-round snapshot and the progress bar's landing animation alike.
 *
 * Stars track *completion*, not a fixed word count, so every lesson fills all 25 whatever its
 * size. Deriving them from WORDS_PER_STAR instead only agrees for a 250-word lesson: on the
 * 100-word picture lesson it lights floor(m/10) stars where floor(m/4) are earned, so the bar
 * showed too few yellow stars while the rocket — which rides `completion` — sat correctly.
 */
export function starsFor(mastered: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(STAR_COUNT, Math.floor((mastered / total) * STAR_COUNT)));
}

/**
 * Max UFO "treats" shown/spendable at once (survival only — 2 per side in the menu).
 *
 * Four, down from six. Six was set when Survival was the only reward there was, and the menu has
 * since grown five more treat kinds: at six the UFOs held all three rows of their column and the
 * saucers read as the menu's wallpaper rather than as one prize among several. Dropping to four
 * frees the third row (x 220/1060, y 480) and evens the columns — every treat now shows at most
 * four, and the two rarest show two.
 *
 * Nothing a child earned is lost by this. Stars keep accruing past the cap and the bank's
 * high-water mark is uncapped, so spending one simply re-reveals the next earned UFO until the
 * bank is drained; the cap only ever governed how many are on screen at once.
 */
/**
 * The 🐱 Cat — the Cat & Mouse letter hunt's treat (specs/cat-mouse-mode.md §2). Earned by reaching
 * **Venus**, the 2nd body of the in-game solar system, in a single game: 250 points at
 * POINTS_PER_STAR = 125, i.e. 25 correct smashes.
 *
 * The lowest rung on the whole treat ladder, and the point of it. The other six start at Mars and
 * cluster upward — 4, 6, 8, 9, 14, 17 — so the app's first hour used to earn nothing at all, and
 * the one mode built for a child who cannot yet read a word sat behind 50 words read correctly.
 * Twenty-five smashes is one or two good boards, and it is reachable inside Picture Words, where
 * the banner is a picture and nothing has to be read cold.
 *
 * **Not star 1** (13 smashes, roughly the first board of the first game a child ever plays): a
 * treat earned before the child has understood that stars *are* earned reads as something the app
 * gave away, and the flourish lands on a player still working out what the screen is. Venus is the
 * first body that needs a second board.
 *
 * Awarded at most ONCE per run (RoundScene.catAwarded); the 2 is checked against the real BODIES
 * order by progression/cat-bonus.test.ts.
 */
export const CAT_AT_STACK_STARS = 2;

/**
 * Max Cats shown/spendable at once — four, matching DIAMOND_MAX, ICE_MAX and PASTA_MAX. Like every
 * bank since the Diamond the earned counter keeps accruing past this, so spending one re-reveals
 * the next. See MenuScene.addCat for where four of them fit, and specs/cat-mouse-mode.md §3.2 for
 * why a seventh treat kind is the last one this menu has room for.
 */
export const CAT_MAX = 4;

/**
 * The 🪱 Space Worm — the letter-eating worm's treat (specs/space-worm-mode.md §2). Earned by
 * reaching **star 13**, where the Sun reddens, in a single game: 1625 points at POINTS_PER_STAR =
 * 125, i.e. 163 correct smashes.
 *
 * The first prize anywhere ABOVE the full stack, and it is there to fill the ladder's longest
 * unpaid climb: the Satellite pays at star 9 and the next thing to happen was the eclipse at 14,
 * with three rungs in between that `milestones.ts` does not even name. 13 is named (`sun_red`), it
 * pays nothing today, and it is the first rung where the Sun changes state rather than brightness.
 *
 * NOT 15 or 16 for this one: those sit within a dozen smashes of the Galaxy at 17. Star 15 was
 * later given to the 🚀 Rocket anyway — a deliberate reversal, see ROCKET_AT_STACK_STARS — and 16
 * stays empty.
 *
 * Awarded at most ONCE per run (RoundScene.wormAwarded); the 13 is milestones.ts's, checked against
 * the real ladder by progression/worm-bonus.test.ts.
 */
export const WORM_AT_STACK_STARS = SUN_RED_STARS;

/**
 * The 🚀 Rocket — Rocket Read's treat (specs/rocket-read-mode.md §2). Earned by reaching **star 15**,
 * where the Sun turns violet, in a single game: 1875 points at POINTS_PER_STAR = 125, i.e. 188
 * correct smashes.
 *
 * This reverses the Worm's "leave 15 and 16 empty", on purpose: the top of the ladder now pays
 * four treats within 50 smashes (13 · 14 · 15 · 17). Accepted because Rocket Read is the hardest
 * minigame — a speed-reading run — and belongs on a rung only a deep run reaches. 16 stays empty.
 *
 * Derived from SUN_VIOLET_STARS, never a bare 15. Awarded at most ONCE per run
 * (RoundScene.rocketAwarded), and only in ROCKET_READ_LANGS.
 */
export const ROCKET_AT_STACK_STARS = SUN_VIOLET_STARS;

/**
 * Max Rockets shown/spendable at once — **two**, for the Worm's reason: 188 smashes is a long run.
 * They take the inner column's second row, the last symmetric pair on the menu (menuSlots.ts).
 */
export const ROCKET_MAX = 2;

/**
 * The languages Rocket Read ships in (specs/rocket-read-mode.md §10) — **all ten**, since every
 * language has a Comprehension pack of 100 sentences and 200 questions, and each one's 200 deal
 * cleanly into a right answer, a rival and at least three outsiders (rocket/deal.test.ts).
 *
 * The list stays rather than becoming "every language", because it is what the Rocket is awarded
 * and drawn on: a language that ever ships without comprehension content must not pay out a treat
 * whose run has nothing to read. `deal.test.ts` walks exactly this list.
 *
 * What differs per language is not whether it runs but how its sentences are MEASURED — see
 * LANG_UNITS in game/rocket/clock.ts, which is why Korean eojeol and Thai clusters get the same
 * 60 s opening sentence as nine German words.
 */
export const ROCKET_READ_LANGS: readonly string[] = [
  'de-CH', 'de', 'en', 'nl', 'fr', 'es', 'it', 'ko', 'ja', 'th',
];

export function isRocketReadLang(lang: string): boolean {
  return ROCKET_READ_LANGS.includes(lang);
}

/**
 * Max Space Worms shown/spendable at once — **two**, not four.
 *
 * 163 smashes is a long run: a player with four banked has played an hour without spending one, so
 * the honest display cap is the number a good session actually produces. Two also leaves the inner
 * column's second row free, which is the **last symmetric pair on the menu** — see
 * specs/space-worm-mode.md §3.2, and `menuSlots.ts` for the arithmetic. Like every bank since the
 * Diamond, the earned counter keeps accruing past this, so spending one re-reveals the next.
 */
export const WORM_MAX = 2;

export const UFO_MAX = 4;

/**
 * The second way to earn a UFO: reach **Mars** — the 4th body of the in-game solar system on the
 * right edge (`BODIES` in src/game/ui/PlanetStack.ts) — in a single game. At POINTS_PER_STAR = 125
 * that is 500 points, i.e. 50 correct smashes in one run.
 *
 * Why a second source at all: the star-earned UFO above needs 3 journey stars = 12% of the lesson
 * MASTERED, and mastery needs MIN_SESSIONS_TO_MASTER sessions — so however well a child played,
 * their first session could never produce one. This one pays for playing well *today*, and since
 * the stack is score-driven it costs the same 50 hits in every lesson whatever its size.
 *
 * Awarded at most ONCE per run (see RoundScene.ufoAwarded), so the planets past Mars don't each
 * pay out again. The 4 is checked against the real BODIES order by progression/ufo-bonus.test.ts.
 */
export const UFO_AT_STACK_STARS = 4;

/**
 * The 💎 Diamond — the Pairs minigame's treat (specs/pairs-mode.md §2). Earned by reaching
 * **Saturn**, the 6th body of the in-game solar system, in a single game: 750 points at
 * POINTS_PER_STAR = 125, i.e. 75 correct smashes — half again the UFO's Mars run.
 *
 * Saturn rather than a further planet because it is the one body a five-year-old can name on
 * sight, and because it stays two rungs short of the full-stack send-off, which must remain the
 * thing above it. Awarded at most ONCE per run (RoundScene.diamondAwarded); the 6 is checked
 * against the real BODIES order by progression/diamond-bonus.test.ts.
 *
 * There is deliberately no second, mastery-driven source the way the UFO has one: that exists
 * because the UFO's star route could not pay out in a first session at all, which is not a problem
 * this treat has — its only route is skill in a single game.
 */
export const DIAMOND_AT_STACK_STARS = 6;

/**
 * Max Diamonds shown/spendable at once — four, one per gap between the six UFO slots on the menu
 * (MenuScene.addDiamond). Independent of UFO_MAX: the two banks never pay for each other. Like the
 * UFO bank the earned counter keeps accruing past this, so spending one re-reveals the next.
 */
export const DIAMOND_MAX = 4;

/**
 * The 🧊 Ice Cube — the Ice Maze minigame's treat (specs/word-maze-mode.md §2). Earned by reaching
 * **Neptune**, the 8th body of the in-game solar system, in a single game: 1000 points at
 * POINTS_PER_STAR = 125, i.e. 100 correct smashes.
 *
 * Three treats, three rungs, evenly spaced at 50 / 75 / 100 smashes — a ladder a child climbs
 * rather than three unrelated prizes. Neptune is also the ice giant, which is where the mode's
 * whole visual language comes from: the letters freeze, the cubes melt, the board shatters.
 *
 * It sits ONE rung below the full-stack send-off rather than two like the Diamond, and that is
 * deliberate at the top of the ladder: a child who banks a cube is told in the same breath that
 * they are 25 smashes from the Sun. Awarded at most ONCE per run (RoundScene.cubeAwarded); the 8
 * is checked against the real BODIES order by progression/ice-bonus.test.ts.
 *
 * One source only, like the Diamond and for the same reason (see DIAMOND_AT_STACK_STARS).
 */
export const ICE_AT_STACK_STARS = 8;

/**
 * Max Ice Cubes shown/spendable at once — four, in the outer column of the menu, outside the six
 * UFO slots (MenuScene.addCube), filled L1 R1 L2 R2 so the column stays balanced. Like the other
 * two banks the earned counter keeps accruing past this, so spending one re-reveals the next and
 * nothing a child earned is ever destroyed.
 */
export const ICE_MAX = 4;

/**
 * The 🌌 Galaxy — the Word Wagon minigame's treat (specs/wagon-mode.md §2). Earned by reaching the
 * **top of the whole planet ladder** in a single game: the star at which PlanetStack stops drawing
 * the Sun and pulls back to a spiral. 17 stars = 2125 points at POINTS_PER_STAR = 125, i.e. 213
 * correct smashes (212.5, and a smash is worth ten whole points, so the 213th is the one that
 * lands it).
 *
 * Deliberately NOT a rung of the 50/75/100 treat ladder. The other three are evenly spaced because
 * they are a staircase; this one is placed by something that already exists — GALAXY_STARS is the
 * last thing the game has to show, it is the one named milestone with no prize on it, and it is
 * already the Scoreboard's endurance star (progression/scoreboard.ts). One run now lights that
 * star and hands over a minigame in the same instant.
 *
 * **Never a bare 17.** The number is milestones.ts's, which is also what PlanetStack's own
 * GALAXY_AT is pinned against by milestones.test.ts — three readers, one constant, so a treat can
 * never end up on a rung the ladder does not draw. Awarded at most ONCE per run
 * (RoundScene.galaxyAwarded); checked by progression/galaxy-bonus.test.ts.
 *
 * One source only, like the Diamond and the Cube (see DIAMOND_AT_STACK_STARS).
 */
export const GALAXY_AT_STACK_STARS = GALAXY_STARS;

/**
 * The 🛰️ Satellite — the Word Labyrinth minigame's treat (specs/labyrinth-mode.md §2). Earned by
 * filling the star stack in a single game: **star 9, the Sun**, 1125 points at POINTS_PER_STAR =
 * 125, i.e. 113 correct smashes (112.5, and a smash is worth ten whole points).
 *
 * "Fill the whole solar system and get a satellite" is the sentence this rung exists to be able to
 * say to a child, and it is why the treat ladder's even 25-smash spacing ends here: the steps run
 * 50 / 75 / 100 / **112.5**, and the last one is half the size of the others. That is a real cost,
 * paid deliberately — the stack ends at nine, and the last treat should be bought by FILLING it
 * rather than by walking past the end of it into the unnamed rungs above.
 *
 * **Never a bare 9.** Derived from STAR_STACK_SLOTS, the same trick GALAXY_AT_STACK_STARS uses on
 * milestones.ts's GALAXY_STARS: one constant, several readers, so a treat can never end up on a
 * rung the ladder does not draw. progression/sat-bonus.test.ts pins it against PlanetStack's own
 * BODIES order.
 *
 * It shares its star with the full-stack send-off (BoardScene.showTrueStar — a rocket and
 * confetti), which is the one thing the other three treats do not have to do. HUDScene therefore
 * sequences the flourish BEHIND that send-off rather than a second behind the star chime; see
 * specs/labyrinth-mode.md §2.1. Awarded at most ONCE per run (RoundScene.satAwarded).
 *
 * One source only, like the Diamond, the Cube and the Galaxy (see DIAMOND_AT_STACK_STARS).
 */
export const SAT_AT_STACK_STARS = STAR_STACK_SLOTS;

/**
 * Max Satellites shown/spendable at once — **two**, stacked on the right of the outer column at
 * (1190, 480) and (1190, 398).
 *
 * It was one, and that was a constraint rather than a choice: the outer column looked full. It was
 * not — the interleaved rows at y 233/398 were free the whole time and only the three rows at
 * 150/315/480 had been reasoned about (specs/labyrinth-mode.md §3.2). With y 398 now known to be
 * available on both sides, the two treats that live at the ends of the ladder take one each and
 * still face each other across the bottom of the menu.
 *
 * Two rather than four because 113 smashes is a rare thing to hold two of, and a row of them would
 * say the opposite of what it costs. Like every other bank the earned counter keeps accruing past
 * this, so nothing a child earned is ever destroyed.
 */
export const SAT_MAX = 3;

/**
 * The 🍝 Plate — the Recipe Kitchen's treat (specs/recipe-mode.md §2). Earned at **star 14, the
 * eclipse**: 1750 points at POINTS_PER_STAR = 125, i.e. 175 correct smashes.
 *
 * The rung is the one EVENT above the Sun. `PlanetStack` recolours the Sun at 13, 15 and 16 and
 * pulls back to a galaxy at 17; only at 14 does something cross it, which is what a treat wants —
 * and "the sun goes dark, so it is time to eat" is the sentence this rung exists to be able to say
 * to a child.
 *
 * **175 smashes is a deep run, and that is deliberate.** The ladder reads 50 / 75 / 100 / 113 /
 * **175** / 213: the top of it is meant to be far, and four earlier treats exist so that a first
 * session is not empty. A sixth reward on a fifth rung would flatten the only part of the ladder
 * whose job is distance.
 *
 * Derived from ECLIPSE_STARS, never a bare 14 — the same trick GALAXY_AT_STACK_STARS uses on
 * GALAXY_STARS. One constant, three readers (the drawing, the naming, this prize), so a treat can
 * never land on a rung the ladder does not draw.
 *
 * One source only, like the Diamond, the Cube, the Galaxy and the Satellite (see
 * DIAMOND_AT_STACK_STARS). Awarded at most ONCE per run (RoundScene.pastaAwarded).
 */
export const PASTA_AT_STACK_STARS = ECLIPSE_STARS;

/**
 * Max Plates shown/spendable at once — **two**, in the outer column's upper gap on each side
 * (specs/recipe-mode.md §3.2).
 *
 * Two rather than one because a Recipe run is short (two to four minutes) and a plate is the treat
 * a child is most likely to want twice running; two rather than four because at 175 smashes apiece
 * a pair of them is already a long evening. Unlike SAT_MAX this is a decision, not a shortage: the
 * menu's y-398 gap slots are still free after it (the ceiling is 20 buttons, not the 16 this file
 * used to imply — see a69841f).
 *
 * Like every other bank the earned counter keeps accruing past this, so nothing a child earned is
 * ever destroyed.
 */
export const PASTA_MAX = 3;

/**
 * Max Galaxies shown/spendable at once — **two**, stacked on the left of the outer column at
 * (90, 480) and (90, 398).
 *
 * It was one, on the argument that the top of the ladder is a single prize and a row of them would
 * say the opposite of what 213 smashes means. Two keeps that: a player who reaches the galaxy twice
 * before spending either has done something the game should not quietly throw away, and two is
 * still nothing like a row. The second slot exists because y 398 turned out to be free on both
 * sides of the outer column (specs/labyrinth-mode.md §3.2) — it was never the shortage it looked
 * like.
 *
 * Like every other bank the earned counter keeps accruing past this, so spending one re-reveals the
 * next and nothing a child earned is ever destroyed.
 */
export const GALAXY_MAX = 3;

/**
 * Leitner boxes 1..MAX_BOX. Spawn weight by box (index 0 = box 1): low box = struggling = shown
 * more. The array's LENGTH is the mastery bar — MAX_BOX / MASTER_BOX derive from it — so a word
 * costs `MAX_BOX - 1` correct answers, spread over MIN_SESSIONS_TO_MASTER sessions.
 *
 * Two things about the shape, both of which cost the journey bar real progress before:
 *
 * 1. **The top box is the mastered one and gets a low maintenance weight (0.4)** — a finished word
 *    only comes back to refresh.
 * 2. **The box one step BELOW it is weighted like a new word (3), not like a nearly-finished one.**
 *    That is the fix. The previous table `[3, 2.5, 2, 1.2, 0.4]` gave that box the second-LOWEST
 *    weight in the whole array — the engine systematically starved exactly the words that were one
 *    correct answer away from becoming a star. Measured over 8 simulated games, 36% of all earned
 *    box-steps (168 of 473 for a grade-1 player) sat stranded in words that never closed.
 *
 * Dropping 5 boxes to 4 does the rest: a word costs 3 correct answers instead of 4, so a star costs
 * 30 sightings instead of 40. Together, measured on the `tools/difficulty` cohorts (stars after 8
 * games, grade1 / grade2): dailylife 7.1/12.0 → 11.2/18.4, reading-250 8.3/13.2 → 11.9/16.1,
 * adventure 6.4/16.0 → 11.8/18.9, picturewords 11.9/22.8 → 20.1/24.0. Topic variety per game does
 * not drop — it rises slightly (7.5 → 8.2), because the boards stop re-drawing capped words.
 *
 * The anti-cramming gate is untouched: MIN_SESSIONS_TO_MASTER still forbids mastering a word in one
 * sitting, so the first session of a fresh journey still earns 0 stars by design.
 */
export const BOX_WEIGHTS = [3, 2.5, 3, 0.4] as const;
export const MAX_BOX = BOX_WEIGHTS.length; // 4

// ── Spacing (how far apart a word's repeats are) ──────────────────────────────
// The box weights above say *how often* a word comes back on average; they say nothing about
// *when*, so a word could land on three boards in a row and then vanish. That is what made long
// sessions feel like the same six words on a loop.
//
// The shape we want: see a new word twice in quick succession (it has to stick), then push the
// repeats further and further apart as it climbs the boxes.

/**
 * Minimum spacing per box, measured in **turns of the word's own topic** (a topic comes round once
 * per cycle through the unlocked topics, so 1 here ≈ `unlockedDrillClasses().length` boards).
 *
 * Boxes 1–2 are the two quick repeats a new word needs; 3 and up widen out. These are *soft* — a
 * not-yet-due word is damped by NOT_DUE_WEIGHT rather than excluded, so a thin topic can still fill
 * a board.
 *
 * One entry per box, so this array is MAX_BOX long. It was `[1, 1, 3, 6, 12]` when there were five
 * boxes; the 12 belonged to the old box 5 and is simply gone with it — boxes 1–4 keep the gaps they
 * always had, which is why the measured gains above come from BOX_WEIGHTS alone.
 */
export const SPACING_GAPS = [1, 1, 3, 6] as const;

/** Weight multiplier for a word that is not yet due (see SPACING_GAPS). */
export const NOT_DUE_WEIGHT = 0.12;

/** Weight multiplier for a word shown on the immediately previous board — near-veto. */
export const JUST_SHOWN_WEIGHT = 0.02;

// ── Per-game repetition ceiling ───────────────────────────────────────────────
// A game ends on lives (STARTING_LIVES), not on a board count, so a strong player can keep going
// indefinitely — without a ceiling the active pool just recycles. Measured before this existed, a
// single game showed one word up to 9 times.

/**
 * Soft ceiling: past this many boards in one game a word's weight collapses, so a 6th or 7th
 * sighting happens only when the pool genuinely has nothing fresher to offer.
 */
export const MAX_SIGHTINGS_PER_GAME = 5;

/** Weight multiplier once a word is past MAX_SIGHTINGS_PER_GAME this game. */
export const OVER_SOFT_CAP_WEIGHT = 0.01;

/**
 * Hard ceiling: a word this often seen in one game is dropped from the draw outright — unless
 * dropping it would leave too few candidates to fill the board, which must never happen.
 */
export const HARD_SIGHTINGS_PER_GAME = 7;

/**
 * Share of a topic's words that must be mastered before it frees its curriculum slot and the next
 * topic unlocks (pools with `maxActiveDrillClasses`).
 *
 * Was effectively 1.0 — *every* word of the topic — which meant one stubborn word jammed the gate
 * and no new topic ever appeared. The remainder keeps drilling in the background; it just no
 * longer blocks the rest of the lesson.
 */
export const TOPIC_UNLOCK_MASTERY = 0.7;

/** Box that counts as mastered. */
export const MASTER_BOX = MAX_BOX; // 4

/** A word cannot be mastered within a single session — anti-cramming gate. */
export const MIN_SESSIONS_TO_MASTER = 2;

/**
 * Classes a board rule may target. Function words (article/pronoun/…) are never targets;
 * they appear as distractors and are mastered by correct avoidance (spec §2.3, O-3).
 */
export const DRILL_CLASSES = ['noun', 'verb', 'adjective'] as const;

/** Per board: how many correct targets and decoys to show. Matches BoardScene's layout. */
export const CORRECT_PER_BOARD = 4;
export const WRONG_PER_BOARD = 4;

/** Minimum correct targets needed to form a board for a drill class. */
export const MIN_CORRECT_PER_BOARD = 2;

/** Share of board slots that may be filled by mastered words (maintenance refresh). */
export const MAINTENANCE_FRACTION = 0.1;
